import { useState, useEffect } from 'react';
import { Coins, Scale, TrendingUp } from 'lucide-react';
import { API_URL } from '../../config/api';

const ScrapReport = ({ getDateRange, reportStoreFilter, formatCurrency }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetch_ = async () => {
      setLoading(true);
      try {
        const dr = getDateRange();
        let params = `start_date=${dr.start}&end_date=${dr.end}`;
        if (reportStoreFilter === 'gold') params += '&store_id=1';
        else if (reportStoreFilter !== 'all' && reportStoreFilter !== 'silver') params += `&store_id=${reportStoreFilter}`;

        const res = await fetch(`${API_URL}/reports/scrap-stats?${params}`);
        if (res.ok) setData(await res.json());
      } catch (e) { console.error(e); }
      finally { setLoading(false); }
    };
    fetch_();
  }, [getDateRange, reportStoreFilter]);

  if (loading) return <div className="empty-state"><div className="empty-icon">⏳</div><p>Hurda verileri yükleniyor...</p></div>;
  if (!data) return <div className="empty-state"><div className="empty-icon">⚖️</div><p>Veri bulunamadı</p></div>;

  const gold = data.byMetal?.find(m => m.metal_type === 'gold') || {};
  const silver = data.byMetal?.find(m => m.metal_type === 'silver') || {};
  const totalCost = (parseFloat(gold.total_cost) || 0) + (parseFloat(silver.total_cost) || 0);
  const totalCount = (gold.count || 0) + (silver.count || 0);

  const months = [...new Set((data.monthly || []).map(m => m.month))].sort();

  return (
    <div className="tab-content">
      <div className="stats-grid">
        <div className="stat-card primary">
          <div className="stat-icon"><Coins size={20} /></div>
          <div className="stat-info">
            <div className="stat-value">{totalCount}</div>
            <div className="stat-label">Toplam Alım</div>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon"><Scale size={20} style={{ color: '#f59e0b' }} /></div>
          <div className="stat-info">
            <div className="stat-value">{parseFloat(gold.total_pure_weight || 0).toFixed(2)} gr</div>
            <div className="stat-label">Altın (Has)</div>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon"><Scale size={20} style={{ color: '#94a3b8' }} /></div>
          <div className="stat-info">
            <div className="stat-value">{parseFloat(silver.total_pure_weight || 0).toFixed(2)} gr</div>
            <div className="stat-label">Gümüş (Has)</div>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon"><TrendingUp size={20} style={{ color: '#e74c3c' }} /></div>
          <div className="stat-info">
            <div className="stat-value">{formatCurrency(totalCost)}</div>
            <div className="stat-label">Toplam Maliyet</div>
          </div>
        </div>
      </div>

      {/* Metal Detay Kartları */}
      <div className="stats-grid">
        {[
          { label: 'Altın', d: gold, color: '#f59e0b', bg: '#fef3c7' },
          { label: 'Gümüş', d: silver, color: '#94a3b8', bg: '#f1f5f9' },
        ].map(m => (
          <div key={m.label} className="chart-card" style={{ flex: 1 }}>
            <h3 style={{ color: m.color }}>{m.label} Hurda</h3>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px 24px', marginTop: 12 }}>
              <div>
                <div style={{ fontSize: 11, color: '#5a6c7d' }}>Alım Adedi</div>
                <div style={{ fontSize: 18, fontWeight: 700 }}>{m.d.count || 0}</div>
              </div>
              <div>
                <div style={{ fontSize: 11, color: '#5a6c7d' }}>Brüt Gram</div>
                <div style={{ fontSize: 18, fontWeight: 700 }}>{parseFloat(m.d.total_gross_weight || 0).toFixed(2)}</div>
              </div>
              <div>
                <div style={{ fontSize: 11, color: '#5a6c7d' }}>Has Gram</div>
                <div style={{ fontSize: 18, fontWeight: 700 }}>{parseFloat(m.d.total_pure_weight || 0).toFixed(2)}</div>
              </div>
              <div>
                <div style={{ fontSize: 11, color: '#5a6c7d' }}>Ort. Saflık</div>
                <div style={{ fontSize: 18, fontWeight: 700 }}>{parseFloat(m.d.avg_purity || 0).toFixed(0)}</div>
              </div>
              <div>
                <div style={{ fontSize: 11, color: '#5a6c7d' }}>Toplam Maliyet</div>
                <div style={{ fontSize: 16, fontWeight: 700, color: '#e74c3c' }}>{formatCurrency(m.d.total_cost || 0)}</div>
              </div>
              <div>
                <div style={{ fontSize: 11, color: '#5a6c7d' }}>Nakit Ödenen</div>
                <div style={{ fontSize: 16, fontWeight: 700 }}>{formatCurrency(m.d.total_cash_paid || 0)}</div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Aylık Trend */}
      {months.length > 1 && (
        <div className="chart-card">
          <h3>Aylık Hurda Alım Trendi</h3>
          <div className="horizontal-bars">
            {months.map((month, i) => {
              const goldM = data.monthly.find(m => m.month === month && m.metal_type === 'gold');
              const silverM = data.monthly.find(m => m.month === month && m.metal_type === 'silver');
              const totalW = parseFloat(goldM?.total_weight || 0) + parseFloat(silverM?.total_weight || 0);
              const totalC = parseFloat(goldM?.total_cost || 0) + parseFloat(silverM?.total_cost || 0);
              const maxCost = Math.max(...months.map(mo => {
                const g = data.monthly.find(m => m.month === mo && m.metal_type === 'gold');
                const s = data.monthly.find(m => m.month === mo && m.metal_type === 'silver');
                return parseFloat(g?.total_cost || 0) + parseFloat(s?.total_cost || 0);
              }), 1);
              return (
                <div key={month} className="h-bar-item">
                  <div className="h-bar-label">
                    <span className="h-bar-name">{month}</span>
                    <span className="h-bar-value">{totalW.toFixed(1)} gr • {formatCurrency(totalC)}</span>
                  </div>
                  <div className="h-bar-track">
                    <div className={`h-bar-fill color-${(i % 5) + 1}`} style={{ width: `${(totalC / maxCost) * 100}%` }} />
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default ScrapReport;
